import { Navigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import { PageHeader, StatusChip } from '@platform/ui-kit';
import type { PortalModule } from '../../api';
import { EntitlementsTab } from '../admin/EntitlementsTab';
import { SHARED_SECTIONS } from '../shared/sharedSections';
import { TalogentUsersTab } from './TalogentUsersTab';

/**
 * Licensing & Usage.
 *
 * Its own bucket: what the client has licensed, and how much of it they are
 * consuming. Entitlements & Licensing lives here rather than under Administration.
 *
 * Administrator-gated like Administration. The API stays under `/api/admin`, which
 * is the administrator-gated surface, so `requireAdmin` covers both from one
 * boundary.
 */
const SECTIONS: Record<string, { title: string; description: string; render: () => JSX.Element }> = {
  'entitlements-licensing': {
    title: 'Entitlements & Licensing',
    description: 'Which modules this install has licensed, and how many seats each has.',
    render: () => <EntitlementsTab />,
  },
  'talogent-users': {
    title: 'Talogent Users',
    description: 'Talogent staff accounts. The only place talogent.com users are listed.',
    render: () => <TalogentUsersTab />,
  },
  // Token Usage and Database Usage are identical to the Administration
  // copies — one definition, not duplicates.
  ...SHARED_SECTIONS,
};

export interface LicensingModuleProps {
  modules: PortalModule[];
}

export function LicensingModule({ modules }: LicensingModuleProps) {
  const { sectionKey } = useParams<{ sectionKey: string }>();

  // `modules` is what THIS user may open, as resolved by the server. If Licensing &
  // Usage is not in it, the user has typed the URL directly — send them away rather
  // than rendering the screen and letting the API return Forbidden behind it. A
  // client must never see so much as the shell of a vendor screen.
  const module = modules.find((m) => m.id === 'licensing_usage');
  if (!module || module.sections.length === 0) return <Navigate to="/" replace />;

  const first = module.sections[0]!.path;

  // Module home redirects to the first section, matching every other module.
  if (!sectionKey) return <Navigate to={first} replace />;

  // Sections are filtered per user too, so a section this user cannot open is
  // treated the same as one that does not exist.
  const permitted = module.sections.some((s) => s.key === sectionKey);
  const section = permitted ? SECTIONS[sectionKey] : undefined;
  if (!section) return <Navigate to={first} replace />;

  return (
    <Box>
      <PageHeader
        title={section.title}
        description={section.description}
        meta={<StatusChip tone="info" label="Administrator only" />}
      />
      {section.render()}
    </Box>
  );
}
